import NotAuthorized from 'components/common/NotAuthorized';
import PleaseWait from 'components/common/PleaseWait';
import { useSession } from 'next-auth/react';
import { isServer } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { isCustomer } from 'services/subscriptionUtils';
import { AgilityPage, ButtonStyle } from 'types/AgilityTypes';
import { logClientException } from 'appinsights/clientAppInsights';
import LayoutContainer from 'components/common/LayoutContainer';
import CommonButton from 'components/common/CommonButton';

// http://localhost:3000/secure-file?fileId=Reality%20Coach%20AI_%20Organizational%20Architecture.pdf

const defaultButtonStyle: ButtonStyle = {
  fields: {
    color: 'primary',
    name: 'Sign In',
    variant: 'standard',
    textColor: '#fff',
  },
  contentID: 123,
  properties: {
    state: 1,
    definitionName: 'SignInButton',
    itemOrder: 1,
    modified: new Date().toISOString(),
    referenceName: 'test',
    versionID: 123,
  },
};

const SecureFile: React.FC = ({ page }: { page: AgilityPage }) => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const queryString = router.asPath.split('?')[1];
  const params = new URLSearchParams(queryString);
  const fileId = params.get('fileId');
  const [waiting, setWaiting] = useState<boolean>(false);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(
    `${fileId} downloading....`,
  );

  useEffect(() => {
    if (
      !isServer &&
      status === 'authenticated' &&
      fileId &&
      fileId.length > 0 &&
      session &&
      session.user &&
      isCustomer(session.user)
    ) {
      downloadFile();
    } else {
      console.log(
        `status: ${status} fileId: ${fileId} isCustomer: ${isCustomer(session?.user)}`,
      );
    }
  }, [isServer, status, session, fileId]);

  if (!fileId || fileId.length === 0) {
    if (!isServer) {
      router.replace(`/404?m=sf&id=${page.pageID}&p=${page.name}`);
      return null; // Prevent further rendering
    }
  }
  if (status === 'loading') {
    return <PleaseWait isLoading={true} />;
  }

  if (status === 'authenticated' && !isCustomer(session.user)) {
    return (
      <div className="mt-8">
        <NotAuthorized />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <LayoutContainer
        size="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: '2rem',
          mb: '2rem',
        }}
      >
        <p className="my-4">Please sign in to access this resource.</p>
        <CommonButton
          text="Sign In"
          link={{
            href: `/signin?redirect=/secure-file?fileId=${fileId}`,
            target: '_self',
          }}
          buttonStyle={defaultButtonStyle}
        />
      </LayoutContainer>
    );
  }

  const downloadFile = async () => {
    try {
      setWaiting(true);
      const response = await fetch(`/api/files/${fileId}/download`);
      if (!response.ok) {
        if (response.status === 404) {
          enqueueSnackbar(`File ${fileId} was not found.`, {
            variant: 'error',
            persist: true,
            action: (key) => (
              <button onClick={() => closeSnackbar(key)}>Dismiss</button>
            ),
          });
          return;
        }
        const errorText = await response.text();
        throw new Error(`Failed to download file: ${fileId}. ${errorText}`);
      }
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileId || '';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      try {
        // Only attempt to close the window if not running in Safari
        const isSafari =
          typeof window !== 'undefined' &&
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (!isSafari) {
          window.close();
        }
      } catch {
        // continue
      }
      setDownloadMessage(`${fileId} downloaded`);
    } catch (error) {
      enqueueSnackbar(`Failed to download file`, {
        variant: 'error',
        persist: true,
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
      logClientException(error);
      console.error('Error downloading the file:', error);
    } finally {
      setWaiting(false);
    }
  };

  return (
    <LayoutContainer
      size="md"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: '2rem',
        mb: '2rem',
      }}
    >
      <PleaseWait isLoading={waiting} />
      <p>{downloadMessage}</p>
    </LayoutContainer>
  );
};

export default SecureFile;
